// src/pages/TradeTracker/components/PortfolioChart.jsx
import React from "react";
import dayjs from "dayjs";
import ChartTooltip from "components/ChartTooltip";
import DateRangeSelector from "components/DateRangeSelector";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

/* ---------------- NUMBER FORMATTING HELPERS ---------------- */

function formatKMB(v) {
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return (v || 0).toLocaleString();
}

function formatWithSymbolKMB(v, symbol) {
  if (!symbol) return formatKMB(v);
  const neg = v < 0;
  const absStr = formatKMB(Math.abs(v));
  return neg ? `-${symbol}${absStr}` : `${symbol}${absStr}`;
}

function formatFull2dp(v, symbol) {
  const n = Number(v) || 0;
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return symbol ? (neg ? `-${symbol}${abs}` : `${symbol}${abs}`) : abs;
}

/* ---------------- NICE SCALE HELPERS (RESTORED) ---------------- */

function niceNum(range, round) {
  const exponent = Math.floor(Math.log10(range || 1));
  const fraction = range / Math.pow(10, exponent);
  let niceFraction;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }

  return niceFraction * Math.pow(10, exponent);
}

function makeNiceScale(min, max, maxTicks = 6) {
  if (!isFinite(min) || !isFinite(max) || min === max) {
    const base = isFinite(min) ? min : 0;
    const span = Math.max(Math.abs(base) * 0.2, 1);
    return makeNiceScale(base - span, base + span, maxTicks);
  }

  const range = niceNum(max - min, false);
  const step = niceNum(range / Math.max(maxTicks - 1, 2), true);
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  const ticks = [];
  for (let v = niceMin; v <= niceMax + 1e-9; v += step) {
    const fixed = Math.abs(step) >= 1 ? Math.round(v) : Number(v.toFixed(10));
    ticks.push(fixed);
  }

  return { niceMin, niceMax, ticks };
}

/* ---------------- MONTHLY TICKS (STRING-BASED) ---------------- */

function subtractMonthClamped(d, step, anchorDay) {
  const candidate = d.subtract(step, "month");
  if (candidate.date() < anchorDay) {
    return candidate.endOf("month");
  }
  return candidate;
}

function chooseMonthStep(months) {
  if (months <= 6) return 1;
  if (months <= 18) return 2;
  if (months <= 36) return 3;
  if (months <= 72) return 6;
  return 12;
}

function generateMonthlyTickLabels(startTs, endTs) {
  const start = dayjs(startTs);
  const end = dayjs(endTs);

  const diffMonths = end.diff(start, "month");
  const step = chooseMonthStep(diffMonths);
  const anchorDay = end.date();

  const ticks = [];
  let d = end.clone();

  while (d.valueOf() >= start.valueOf()) {
    ticks.push(d.format("YYYY-MM-DD")); // STRING tick label
    d = subtractMonthClamped(d, step, anchorDay);
  }

  return ticks.reverse();
}

/* ---------------- COMPONENT ---------------- */

export default function PortfolioChart({
  plotRef,
  chartData = [],
  chartMode = "valuation",
  setChartMode,
  padPct = 0.05,
  notionalAllUSD = false,
  onRangeChange,
  seriesFrom,
  seriesTo,
}) {
  const dataKey = chartMode === "valuation" ? "valuation" : "notional";

  const [chartStartDate, setChartStartDate] = React.useState("");
  const [chartEndDate, setChartEndDate] = React.useState("");

  React.useEffect(() => {
    if (seriesFrom) setChartStartDate(seriesFrom);
    if (seriesTo) setChartEndDate(seriesTo);
  }, [seriesFrom, seriesTo]);

  const handleApplyRange = React.useCallback(
    (from, to) => {
      if (!from && !to) {
        setChartStartDate("");
        setChartEndDate("");
        onRangeChange?.(null, null);
        return;
      }
      setChartStartDate(from || "");
      setChartEndDate(to || "");
      if (from && to) onRangeChange?.(from, to);
    },
    [onRangeChange]
  );

  /* ---------------- FILTER DATA ---------------- */

  const filteredData = React.useMemo(() => {
    if (!chartData?.length) return [];

    const startTs = chartStartDate ? dayjs(chartStartDate).valueOf() : -Infinity;
    const endTs = chartEndDate ? dayjs(chartEndDate).valueOf() : Infinity;

    return chartData
      .filter((d) => {
        const ts = Number(d.ts) || 0;
        return ts >= startTs && ts <= endTs;
      })
      .map((d) => ({
        ...d,
        label: dayjs(d.ts).format("YYYY-MM-DD"), // for category x-axis
      }));
  }, [chartData, chartStartDate, chartEndDate]);

  /* ---------------- Y-AXIS SCALE (USING NICE SCALE) ---------------- */

  const values = filteredData.map((d) => Number(d?.[dataKey]) || 0);
  const hasData = values.length > 0;

  const statistics = React.useMemo(() => {
    if (chartMode !== "valuation") return null;

    const observations = filteredData
      .map((d) => ({
        value: Number(d?.[dataKey]),
        date: d.ts,
      }))
      .filter((observation) => Number.isFinite(observation.value));

    if (!observations.length) return null;

    const total = observations.reduce((sum, observation) => sum + observation.value, 0);
    const min = observations.reduce((current, observation) =>
      observation.value < current.value ? observation : current
    );
    const max = observations.reduce((current, observation) =>
      observation.value > current.value ? observation : current
    );
    const average = total / observations.length;
    const variance = observations.reduce(
      (sum, observation) => sum + Math.pow(observation.value - average, 2),
      0
    ) / observations.length;

    return {
      min,
      average,
      max,
      standardDeviation: Math.sqrt(variance),
    };
  }, [filteredData, dataKey, chartMode]);

  let yDomain = [-1, 1];
  let yTicks = [-1, 0, 1];

  if (hasData) {
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);

    const span = dataMax - dataMin || Math.abs(dataMax) || 1;
    const pad = span * padPct;

    let lo = dataMin - pad;
    let hi = dataMax + pad;

    if (dataMin >= 0) lo = 0;
    if (dataMax <= 0) hi = 0;

    const { niceMin, niceMax, ticks } = makeNiceScale(lo, hi, 6);

    yDomain = [niceMin, niceMax];
    yTicks = ticks;
  }

  const currencySymbol =
    chartMode === "valuation" ? "$" : notionalAllUSD ? "$" : "";

  const yTickFormatter = (v) => formatWithSymbolKMB(v, currencySymbol);

  const tooltipFormatter = (val, name) => [
    formatFull2dp(val, currencySymbol),
    name === "valuation" ? "Value" : "Notional",
  ];

  /* ---------------- X-AXIS STRING TICKS ---------------- */

  const xTicks = React.useMemo(() => {
    if (!filteredData.length) return [];
    const tsValues = filteredData.map((d) => d.ts);
    const minTs = Math.min(...tsValues);
    const maxTs = Math.max(...tsValues);
    return generateMonthlyTickLabels(minTs, maxTs);
  }, [filteredData]);

  /* ---------------- RENDER ---------------- */

  return (
    <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
        <h3 className="mb-3 text-lg font-semibold text-brand">
          Historical {chartMode === "valuation" ? "Value" : "Notional"}
        </h3>

        <div className="flex flex-wrap items-center gap-3">
          <DateRangeSelector defaultRange="90" onApplyFilters={handleApplyRange} />

          <div className="inline-flex items-center rounded-md border border-border overflow-hidden">
            {["valuation", "notional"].map((m) => {
              const active = chartMode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setChartMode?.(m)}
                  aria-pressed={active}
                  className={
                    "px-3 py-1 text-sm font-medium transition-colors " +
                    (active
                      ? "btn btn-primary"
                      : "bg-app text-muted hover:text-app hover:bg-app/10")
                  }
                >
                  {m === "valuation" ? "Value" : "Notional"}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div
        className="flex w-full h-[320px] gap-3 p-4 rounded-xl overflow-hidden border border-border bg-app shadow-sm"
      >
        <div className="min-w-0 flex-1">
          <ResponsiveContainer>
            <LineChart
              ref={plotRef}
              data={filteredData}
              margin={{ left: 20, right: 16, top: 8, bottom: 8 }} // extra left margin for Y labels
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="label"                 // STRING axis (fixes UTC issues)
                type="category"
                ticks={xTicks}                  // STRING ticks
                tickFormatter={(s) => dayjs(s).format("M/D/YYYY")}
                tick={{ fill: "hsl(var(--muted-foreground))" }}
              />

              <YAxis
                domain={yDomain}
                ticks={yTicks}
                tickFormatter={yTickFormatter}
                tick={{ fill: "hsl(var(--muted-foreground))" }}
              />

              <Tooltip content={<ChartTooltip formatter={tooltipFormatter} />} />
              <Legend />

              <Line
                type="monotone"
                dataKey={dataKey}
                stroke="var(--chart-1)"
                strokeWidth={3}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {statistics && (
          <div className="w-44 shrink-0 self-start rounded-lg border border-border bg-card/95 p-3 shadow-sm">
            <dl className="space-y-2 text-sm">
              {[
                ["Max.", statistics.max.value, statistics.max.date],
                ["Min.", statistics.min.value, statistics.min.date],
                ["Average", statistics.average, null],
                ["Std. Dev.", statistics.standardDeviation, null],
              ].map(([label, value, date]) => (
                <div key={label}>
                  <dt className="text-xs text-muted">
                    {label}
                    {date && ` — ${dayjs(date).format("M/D/YYYY")}`}
                  </dt>
                  <dd className="font-semibold tabular-nums text-app">
                    {formatFull2dp(value, currencySymbol)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
